import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Copy, Trash2, UserPlus, Monitor, ShieldAlert, Check } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import api, { formatApiErrorDetail } from "../../lib/api";

const when = (s) => (s ? new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const heading = "text-xs uppercase tracking-[0.15em] text-brand-tertiary border-b border-brand-border pb-2";

function Stat({ label, value, testid }) {
  return (
    <div className="border border-brand-border bg-brand-surface px-4 py-3 flex-1">
      <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">{label}</p>
      <p className="font-serif text-2xl text-brand-ink mt-1" data-testid={testid}>{value}</p>
    </div>
  );
}

const approve = async (rid, emails) => {
  try {
    const { data } = await api.post(`/reports/${rid}/recipients`, { emails });
    toast.success(`${data.added} email${data.added === 1 ? "" : "s"} approved`, { description: data.skipped ? `${data.skipped} already on the list` : undefined });
    return true;
  } catch (err) { toast.error("Couldn't approve", { description: formatApiErrorDetail(err.response?.data?.detail) }); return false; }
};

function BulkAdd({ rid, onAdded }) {
  const [text, setText] = useState("");
  const submit = async () => { if (await approve(rid, text)) { setText(""); onAdded(); } };
  return (
    <div className="space-y-2" data-testid="views-bulk-form">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="views-bulk-input"
        placeholder={"Paste emails, separated by commas or new lines\njane@fund.com, John Smith <john@capital.com>"}
        className="w-full bg-brand-page border border-brand-border rounded px-2.5 py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary resize-none" />
      <button onClick={submit} disabled={!text.trim()} data-testid="views-bulk-submit" className="inline-flex items-center gap-1.5 rounded bg-brand-primary text-brand-page px-3 py-2 text-sm hover:bg-brand-primaryHover disabled:opacity-50"><UserPlus className="h-4 w-4" /> Approve emails</button>
    </div>
  );
}

function PersonRow({ email, sub, badge, testid, children }) {
  return (
    <div className="flex items-center gap-3 py-3" data-testid={testid}>
      <div className="min-w-0 flex-1"><p className="text-sm text-brand-ink truncate">{email}</p><p className="text-[11px] text-brand-tertiary truncate">{sub}</p></div>
      {badge}
      {children}
    </div>
  );
}

const Badge = ({ n, testid }) => (
  <span data-testid={testid} className={`shrink-0 text-[11px] rounded-full px-2 py-0.5 ${n ? "bg-brand-pos/10 text-brand-pos" : "bg-brand-gold/10 text-brand-gold"}`}>{n ? `${n} view${n > 1 ? "s" : ""}` : "Unopened"}</span>
);

function ApprovedList({ data, rid, reload }) {
  const del = async (rc) => {
    if (!window.confirm(`Remove ${rc.email} from the approved list?`)) return;
    await api.delete(`/reports/${rid}/recipients/${rc.id}`); reload();
  };
  return (
    <div className="divide-y divide-brand-border">
      {data.recipients.map((rc) => (
        <PersonRow key={rc.id} email={rc.name ? `${rc.name} · ${rc.email}` : rc.email} testid={`views-recipient-${rc.email}`}
          sub={rc.views ? `Last opened ${when(rc.last_viewed)}` : "Not opened yet"} badge={<Badge n={rc.views} testid={`views-recipient-count-${rc.email}`} />}>
          <button onClick={() => del(rc)} title="Remove" data-testid={`views-remove-${rc.email}`} className="p-1.5 text-brand-tertiary hover:text-brand-neg"><Trash2 className="h-4 w-4" /></button>
        </PersonRow>
      ))}
      {data.other_viewers.map((o) => (
        <PersonRow key={o.email} email={o.email} testid={`views-other-${o.email}`} sub={`Opened before list was set · last ${when(o.last_viewed)}`} badge={<Badge n={o.views} />} />
      ))}
    </div>
  );
}

function BlockedList({ blocked, rid, reload }) {
  if (!blocked.length) return <p className="text-xs text-brand-tertiary py-3" data-testid="views-blocked-empty">No blocked attempts.</p>;
  return (
    <div className="divide-y divide-brand-border" data-testid="views-blocked">
      {blocked.map((b) => (
        <PersonRow key={b.id} email={b.email} testid={`views-blocked-${b.id}`} sub={`${when(b.attempted_at)} · ${b.device} · ${b.ip || "unknown IP"}`}
          badge={<ShieldAlert className="h-4 w-4 text-brand-neg shrink-0" />}>
          <button onClick={async () => { if (await approve(rid, b.email)) reload(); }} data-testid={`views-blocked-approve-${b.id}`}
            className="inline-flex items-center gap-1 text-[11px] rounded-full border border-brand-primary text-brand-primary px-2 py-0.5 hover:bg-brand-primary hover:text-brand-page"><Check className="h-3 w-3" /> Approve</button>
        </PersonRow>
      ))}
    </div>
  );
}

function ActivityLog({ views }) {
  if (!views.length) return <p className="text-xs text-brand-tertiary py-3" data-testid="views-empty">No one has opened this report yet.</p>;
  return (
    <div className="divide-y divide-brand-border" data-testid="views-log">
      {views.map((v) => (
        <div key={v.id} className="py-2.5 flex items-start gap-3" data-testid={`views-log-${v.id}`}>
          <Monitor className="h-4 w-4 text-brand-tertiary mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-brand-ink truncate">{v.name ? `${v.name} · ` : ""}{v.email}</p>
            <p className="text-[11px] text-brand-tertiary truncate">{v.device} · {v.ip || "unknown IP"}</p>
          </div>
          <span className="text-[11px] text-brand-muted shrink-0">{when(v.viewed_at)}</span>
        </div>
      ))}
    </div>
  );
}

export function ReportViews({ rid, shareToken, published }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/reports/${rid}/views`).then(({ data: d }) => setData(d)).catch(() => {}), [rid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const copy = async () => {
    const url = `${window.location.origin}/r/${shareToken}`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied", { description: url }); }
    catch { toast.error("Couldn't copy the link"); }
  };
  const blockedN = data?.blocked?.length || 0;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="report-views-btn" className="inline-flex items-center gap-2 text-sm px-4 py-2 text-brand-muted hover:text-brand-primary transition-colors">
        <Eye className="h-4 w-4" /> Views{data ? ` (${data.total_views})` : ""}
        {blockedN > 0 && <span data-testid="report-views-blocked-count" className="text-[10px] rounded-full bg-brand-neg/10 text-brand-neg px-1.5">{blockedN} blocked</span>}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="bg-brand-page w-full sm:max-w-lg overflow-y-auto" data-testid="report-views-panel">
          <SheetHeader>
            <SheetTitle className="font-serif text-2xl font-normal text-brand-ink">Access &amp; views</SheetTitle>
            <SheetDescription className="text-brand-muted">Everyone uses the same link and confirms their email. Add approved emails to restrict access; leave the list empty to let anyone with the link view.</SheetDescription>
          </SheetHeader>
          {data && (
            <div className="mt-6 space-y-7">
              <div className="flex gap-3">
                <Stat label="Total opens" value={data.total_views} testid="views-total" />
                <Stat label="Unique viewers" value={data.unique_viewers} testid="views-unique" />
                <Stat label="Blocked" value={blockedN} testid="views-blocked-total" />
              </div>
              <div className="flex items-center justify-between gap-3">
                <span data-testid="views-access-mode" className={`text-[11px] rounded-full px-2 py-0.5 ${data.restricted ? "bg-brand-primary/10 text-brand-primary" : "bg-brand-gold/10 text-brand-gold"}`}>
                  {data.restricted ? `Restricted · ${data.recipients.length} approved` : "Open · anyone with the link"}
                </span>
                {shareToken && <button onClick={copy} data-testid="views-copy-link" className="inline-flex items-center gap-1.5 text-sm text-brand-primary"><Copy className="h-4 w-4" /> Copy share link</button>}
              </div>
              {!published && <p className="text-xs text-brand-gold" data-testid="views-draft-note">This report is a draft. The link won't open until you publish.</p>}
              <section className="space-y-3">
                <p className={heading}>Approved emails ({data.recipients.length})</p>
                <BulkAdd rid={rid} onAdded={load} />
                <ApprovedList data={data} rid={rid} reload={load} />
              </section>
              <section>
                <p className={heading}>Blocked attempts ({blockedN})</p>
                <BlockedList blocked={data.blocked} rid={rid} reload={load} />
              </section>
              <section>
                <p className={heading}>Recent activity</p>
                <ActivityLog views={data.views} />
              </section>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
